import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import Modal from './Modal';
import { FieldComparison, CustomerSyncComparisonResponse, CustomerSyncUpdateResponse } from '../types';

interface StripeCustomerSyncEditorProps {
  customerId: number;
  currentStripeCustomerId: string | null | undefined;
  companyId?: number;
  onUpdated: (stripeCustomerId: string | null) => void;
  onClose: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const StripeCustomerSyncEditor: React.FC<StripeCustomerSyncEditorProps> = ({
  customerId,
  currentStripeCustomerId,
  companyId: propCompanyId,
  onUpdated,
  onClose,
  onError,
  onSuccess,
}) => {
  const [comparison, setComparison] = useState<CustomerSyncComparisonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [createIfNotExists, setCreateIfNotExists] = useState(true);

  // Use the provided companyId
  const companyId = propCompanyId;

  // Load comparison data
  const loadComparison = useCallback(async () => {
    setLoading(true);
    try {
      const response: CustomerSyncComparisonResponse = await apiService.customers.getCustomerSyncComparison(customerId, companyId);
      setComparison(response);
      
      // Select fields that are recommended for sync
      const recommendedFields = response.comparisons
        .filter((c: FieldComparison) => c.sync_recommended)
        .map((c: FieldComparison) => c.field_name);
      setSelectedFields(recommendedFields);
      setSelectAll(recommendedFields.length > 0);
      
    } catch (error) {
      onError(`Failed to load comparison: ${error}`);
    } finally {
      setLoading(false);
    }
  }, [customerId, companyId, onError]);

  useEffect(() => {
    loadComparison();
  }, [loadComparison]);

  // Toggle field selection
  const toggleField = useCallback((fieldName: string) => {
    setSelectedFields(prev => {
      const newFields = prev.includes(fieldName)
        ? prev.filter((f: string) => f !== fieldName)
        : [...prev, fieldName];
      return newFields;
    });
  }, []);

  // Toggle select all
  const toggleSelectAll = useCallback(() => {
    if (comparison) {
      const allSyncableFields = comparison.comparisons
        .filter((c: FieldComparison) => c.can_sync_to_stripe)
        .map((c: FieldComparison) => c.field_name);
      setSelectedFields(selectAll ? [] : allSyncableFields);
      setSelectAll(!selectAll);
    }
  }, [comparison, selectAll]);

  // Handle sync
  const handleSync = useCallback(async () => {
    if (!selectedFields.length) {
      onError('Please select at least one field to sync');
      return;
    }

    setSyncing(true);
    try {
      const response: CustomerSyncUpdateResponse = await apiService.customers.syncCustomerToStripe(
        customerId,
        selectedFields,
        currentStripeCustomerId || undefined,
        createIfNotExists,
        companyId
      );
      
      if (response.success) {
        onSuccess(`Successfully synced ${response.synced_fields.length} field(s) to Stripe`);
        if (response.synced_fields.length > 0) {
          onUpdated(response.stripe_customer_id);
        }
        if (response.failed_fields.length > 0) {
          onError(`Some fields failed to sync: ${response.failed_fields.join(', ')}`);
        }
        onClose();
      } else {
        onError(response.message || 'Sync failed');
      }
    } catch (error) {
      onError(`Failed to sync: ${error}`);
    } finally {
      setSyncing(false);
    }
  }, [customerId, selectedFields, currentStripeCustomerId, createIfNotExists, companyId, onSuccess, onError, onClose, onUpdated]);

  // Get comparison for a field
  const getFieldComparison = (fieldName: string): FieldComparison | null => {
    return comparison?.comparisons.find((c: FieldComparison) => c.field_name === fieldName) || null;
  };

  // Count stats
  const getStats = () => {
    if (!comparison) return { total: 0, diff: 0, systemOnly: 0, stripeOnly: 0, match: 0 };
    
    return comparison.comparisons.reduce((acc: { total: number; diff: number; systemOnly: number; stripeOnly: number; match: number }, c: FieldComparison) => {
      if (c.in_system && !c.in_stripe) {
        acc.systemOnly++;
      } else if (!c.in_system && c.in_stripe) {
        acc.stripeOnly++;
      } else if (c.in_system && c.in_stripe && c.system_value !== c.stripe_value) {
        acc.diff++;
      } else if (c.in_system && c.in_stripe && c.system_value === c.stripe_value) {
        acc.match++;
      }
      acc.total++;
      return acc;
    }, { total: 0, diff: 0, systemOnly: 0, stripeOnly: 0, match: 0 });
  };

  const stats = getStats();

  if (loading) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Loading Customer Sync Comparison">
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p>Loading comparison data...</p>
        </div>
      </Modal>
    );
  }

  if (!comparison) {
    return (
      <Modal isOpen={true} onClose={onClose} title="Customer Sync Comparison">
        <div style={{ padding: '20px' }}>
          <p>Failed to load comparison data.</p>
          <button className="btn btn-secondary" onClick={loadComparison}>
            Retry
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={onClose} title="Sync Customer Data with Stripe" size="lg">
      <div style={{ padding: '20px' }}>
        {/* Summary Stats */}
        <div className="card" style={{ marginBottom: '20px', padding: '15px' }}>
          <h5>Comparison Summary</h5>
          <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-primary)' }}>
                {stats.diff}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                Differ
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-success)' }}>
                {stats.match}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                Match
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-info)' }}>
                {stats.systemOnly}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                Only in System
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-warning)' }}>
                {stats.stripeOnly}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                Only in Stripe
              </div>
            </div>
          </div>
          
          {!comparison.has_stripe_customer && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: 'var(--color-bg-warning)', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={createIfNotExists}
                  onChange={(e) => setCreateIfNotExists(e.target.checked)}
                  disabled={!comparison.can_create_stripe_customer}
                />
                <span>
                  Create new Stripe customer if it doesn't exist
                  {!comparison.can_create_stripe_customer && (
                    <span style={{ color: 'var(--color-danger)', marginLeft: '5px' }}>
                      (Stripe credentials not configured)
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Field Comparison Table */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', borderBottom: '1px solid var(--color-border)' }}>
            <h5>Field Comparison</h5>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={toggleSelectAll}
              />
              <span>Select All Syncable Fields</span>
            </label>
          </div>
          
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>Field</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>System Value</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid var(--color-border)' }}>Stripe Value</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid var(--color-border)' }}>Sync to Stripe</th>
                </tr>
              </thead>
              <tbody>
                {comparison.comparisons.map((field: FieldComparison, index: number) => (
                  <tr
                    key={field.field_name}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      backgroundColor: index % 2 === 0 ? 'transparent' : 'var(--color-bg-secondary)',
                    }}
                  >
                    <td style={{ padding: '12px' }}>
                      <strong>{field.field_label}</strong>
                      <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                        {field.field_name}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {field.in_system ? (
                        <span style={{ color: field.sync_recommended ? 'var(--color-success)' : 'inherit' }}>
                          {field.system_value || <em style={{ color: 'var(--color-text-secondary)' }}>empty</em>}
                        </span>
                      ) : (
                        <em style={{ color: 'var(--color-text-secondary)' }}>not set</em>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {comparison.has_stripe_customer ? (
                        field.in_stripe ? (
                          <span style={{ color: field.sync_recommended ? 'var(--color-warning)' : 'inherit' }}>
                            {field.stripe_value || <em style={{ color: 'var(--color-text-secondary)' }}>empty</em>}
                          </span>
                        ) : (
                          <em style={{ color: 'var(--color-text-secondary)' }}>not set</em>
                        )
                      ) : (
                        <em style={{ color: 'var(--color-text-secondary)' }}>No Stripe customer</em>
                      )}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      {field.can_sync_to_stripe ? (
                        <input
                          type="checkbox"
                          checked={selectedFields.includes(field.field_name)}
                          onChange={() => toggleField(field.field_name)}
                          disabled={!field.in_system}
                        />
                      ) : (
                        <span style={{ color: 'var(--color-text-secondary)' }}>–</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={syncing}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing || selectedFields.length === 0}
          >
            {syncing ? 'Syncing...' : `Sync ${selectedFields.length} Field(s) to Stripe`}
          </button>
        </div>

        {/* Help Text */}
        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--color-bg-info)', borderRadius: '4px', fontSize: '13px' }}>
          <p style={{ margin: 0 }}>
            <strong>How this works:</strong>
          </p>
          <ul style={{ margin: '10px 0 0 20px', padding: 0 }}>
            <li>Fields marked in <span style={{ color: 'var(--color-success)' }}>green</span> in System are recommended to sync</li>
            <li>Fields marked in <span style={{ color: 'var(--color-warning)' }}>orange</span> in Stripe differ from System</li>
            <li>Only fields with data in System can be synced to Stripe</li>
            <li>Check the boxes to select which fields to update in Stripe</li>
          </ul>
        </div>
      </div>
    </Modal>
  );
};

export default StripeCustomerSyncEditor;
